import { useDrop, DropTargetMonitor, useDrag, DragSourceMonitor } from 'react-dnd';

interface DragItem {
  id?: string;
  index: number;
  type?: string;
}

interface UseSortDropProps {
  ref: React.RefObject<HTMLElement>;
  index: number;
  moveItem: (fromIndex: number, toIndex: number) => void;
  name?: string;
}

type Identifier = string | symbol | null;

const useSortDrop = ({ ref, index, moveItem, name }: UseSortDropProps) => {
  const [collected, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier }
  >({
    accept: name || 'item',
    collect: (monitor: DropTargetMonitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover: (item: DragItem, monitor: DropTargetMonitor) => {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  return [collected, drop] as const;
};

interface DragItem {
  id?: string;
  index: number;
  type?: string;
}

interface UseSortDragProps {
  id?: string;
  index: number;
  name?: string;
}

const useSortDrag = ({ id, index, name }: UseSortDragProps) => {
  const [collected, drag, preview] = useDrag<
    DragItem, 
    void,
    { isDragging: boolean }
  >({
    type: name || 'item',
    item: () => ({ id, index }),
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return [collected, drag, preview] as const;
};

export { useSortDrop, useSortDrag };
